<script setup lang="ts">
import { computed, ref, type Component } from "vue";
import { Sun, Moon, Monitor, Check } from "@lucide/vue";
import Popover from "@/components/Reusables/Popover/Popover.vue";
import { useDarkModeStore } from "@/stores/darkMode.store";

type ColorMode = "light" | "dark" | "auto";

withDefaults(defineProps<{ placement?: "bottom-end" | "top-end" }>(), { placement: "bottom-end" });

const darkModeStore = useDarkModeStore();
const group = ref<HTMLElement | null>(null);

// Radio-group keys: arrows move focus between the cards; Enter/Space select the focused one.
function moveFocus(event: KeyboardEvent, step: number) {
  const cards = Array.from(group.value?.querySelectorAll<HTMLElement>('[role="radio"]') ?? []);
  const index = cards.indexOf(event.target as HTMLElement);
  if (index < 0) return;
  event.preventDefault();
  cards[(index + step + cards.length) % cards.length]?.focus();
}

type Tone = "light" | "dark";

const OPTIONS: { value: ColorMode; label: string; icon: Component; tones: Tone[] }[] = [
  { value: "light", label: "Light", icon: Sun, tones: ["light"] },
  { value: "dark", label: "Dark", icon: Moon, tones: ["dark"] },
  { value: "auto", label: "System", icon: Monitor, tones: ["light", "dark"] },
];

const TONE_CLASSES: Record<Tone, { page: string; bar: string; line: string; chip: string }> = {
  light: {
    page: "bg-theme-preview-light-page",
    bar: "bg-theme-preview-light-bar",
    line: "bg-theme-preview-light-line",
    chip: "bg-theme-preview-light-chip",
  },
  dark: {
    page: "bg-theme-preview-dark-page",
    bar: "bg-theme-preview-dark-bar",
    line: "bg-theme-preview-dark-line",
    chip: "bg-theme-preview-dark-chip",
  },
};

const current = computed(() => OPTIONS.find((option) => option.value === darkModeStore.mode) ?? OPTIONS[0]);
</script>

<template>
  <Popover
    :placement="placement"
    role="dialog"
    aria-label="Appearance"
    panel-class="w-auto">
    <template #trigger="{ toggle, isOpen }">
      <button
        type="button"
        aria-haspopup="dialog"
        :aria-label="`Appearance: ${current.label}`"
        :aria-expanded="isOpen"
        class="bg-slate-0 text-type-default ring-slate-250 p-px-8 focus-ring interactive-active duration-fast ease-standard rounded-full ring-1 transition-all hover:bg-slate-100 hover:ring-slate-300"
        @click="toggle">
        <component
          :is="current.icon"
          class="icon-sm"
          :stroke-width="1.5"
          aria-hidden="true" />
      </button>
    </template>
    <template #default="{ close }">
      <div class="p-px-12 gap-px-12 flex flex-col">
        <p class="t-caption-bold text-type-muted tracking-wide uppercase">Appearance</p>
        <div
          ref="group"
          role="radiogroup"
          aria-label="Appearance"
          class="gap-px-8 flex"
          @keydown.right="moveFocus($event, 1)"
          @keydown.down="moveFocus($event, 1)"
          @keydown.left="moveFocus($event, -1)"
          @keydown.up="moveFocus($event, -1)">
          <button
            v-for="option in OPTIONS"
            :key="option.value"
            type="button"
            role="radio"
            :tabindex="darkModeStore.mode === option.value ? 0 : -1"
            :aria-checked="darkModeStore.mode === option.value"
            class="focus-ring group gap-px-6 rounded-8 flex flex-col items-center"
            @click="
              darkModeStore.setMode(option.value);
              close();
            ">
            <span
              class="duration-fast ease-standard rounded-8 relative block h-14 w-20 overflow-hidden ring-2 transition-all group-hover:-translate-y-px"
              :class="darkModeStore.mode === option.value ? 'ring-app-blue-600' : 'ring-slate-200 group-hover:ring-slate-300'">
              <span
                v-for="(tone, index) in option.tones"
                :key="tone"
                class="absolute inset-0 flex"
                :class="[TONE_CLASSES[tone].page, index > 0 && '[clip-path:polygon(100%_0,100%_100%,0_100%)]']"
                aria-hidden="true">
                <span
                  class="w-3 shrink-0"
                  :class="TONE_CLASSES[tone].bar"></span>
                <span class="gap-px-4 p-px-6 flex grow flex-col">
                  <span
                    class="h-1.5 w-8 rounded-full"
                    :class="TONE_CLASSES[tone].line"></span>
                  <span
                    class="h-1.5 w-11 rounded-full"
                    :class="TONE_CLASSES[tone].line"></span>
                  <span
                    class="mt-auto h-2 w-5 rounded-full"
                    :class="TONE_CLASSES[tone].chip"></span>
                </span>
              </span>
              <span
                v-if="darkModeStore.mode === option.value"
                class="bg-app-blue-600 dark:bg-app-blue-fill absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full text-white"
                aria-hidden="true">
                <Check
                  class="icon-xs"
                  :stroke-width="2.5" />
              </span>
            </span>
            <span
              class="t-caption gap-px-4 flex items-center"
              :class="darkModeStore.mode === option.value ? 'text-type-default' : 'text-type-muted'">
              <component
                :is="option.icon"
                class="icon-xs"
                :stroke-width="1.5"
                aria-hidden="true" />
              {{ option.label }}
            </span>
          </button>
        </div>
      </div>
    </template>
  </Popover>
</template>
